import { useState, useEffect, useRef } from 'react';
import { ISpuInfo, IGroup, IOption, IVariant, ITrackVariant, IProduct, IGift } from 'base/interfaces/product';
import { postAddCart } from '@base/product/services';
import { getStoreState, useStore } from 'base/store';
import { trackProductImpression, trackAddToCart, trackPageView } from 'base/utils/tracker';
import { useRouter } from 'next/router';
import Message from '@base/lib/Message';
import { getVariantImageSrc } from '@base/product/utils';
import { buySkipCart, getAddOnsSrcList, getVariantSummary } from 'base/utils/product';
import { calculateCouponList } from 'mod/coupon/service';
import { ICoupon } from 'mod/coupon/interface';
import { DEBUG_SOURCEMAP, CODE_FLUSH_PAGE } from 'base/config/constant';
import { getDetailFeatureConfig } from 'base/servicesV2/feature';
import { IFeature, IOldFeatureBanner } from 'mod/feature/interface';
import { msgModal } from '@base/lib/ThemeModal';
import useAriaLive from 'base/hooks/useAriaLive'
import { formatPrice } from 'base/utils/price'
import { checkBuyNowHandle } from 'base/utils/checkout'
import { getDefaultVariant, getOnSaleIds } from 'mod/product/utils';

export type IAddCartInstance = {
    trackModuleId?: string;
    variants: IVariant[];//variant列表，用于计算未选中variant时展示的价格
    isFormHidden: boolean; //是否显示选项
    onSaleIds: string[]; //未下架且有库存的variant id
    groups: IGroup[]; //商品属性
    shownVariant: IVariant; //已选中variant,
    checkedVariant: IVariant | undefined; //当前展示信息的variant,
    onCheckOption: (groupIndex: number, option: IOption) => void;//点击选项
    qty: number;
    onChangeQty: (qty: number) => void;
    checkedAddOns: string[];
    onChangeCheckedAddOns: (ids: string[]) => void;
    checkedGift?: IGift;
    setCheckedGift: (gift?: IGift) => void;
    isErrorShown: boolean; //提示表单错误
    disableBuyNow: boolean; //是否禁用buy now
    onSubmit: () => Promise<void>; // options 选择提交
    onSubmitWithVariant: (variant: IVariant | IProduct, num: number) => Promise<void>; // 单个variant 传参提交
    submitting: boolean; //加车表单提交状态
    buyNowLoading: boolean; //buy now 请求状态
    outOfStock: boolean;
    productType?: 'kit' | 'product';
    onBuyNow: () => Promise<void>;
    isDealer: boolean;
    availableCoupons: ICoupon[];
    feature: {
        'product:feature:topRight'?: IFeature[];
        'product:feature:middle'?: IFeature[];
    },
};

type IUseAddCartParams = {
    module?: 'detail'; //区分模块或者页面
    trackModuleId?: IAddCartInstance['trackModuleId'];
    spuInfo: ISpuInfo;
    defaultThumbSize?: number;
    isHiddenOption?: boolean;
    changeUrlWhenCheckOption?: boolean;
    disableBuyNow?: boolean;
    showBanner?: boolean;
    getThumbSrc?: () => (string | void);//获取商品展示大图尺寸
    animateDelay?: number; //动画延迟执行的时间，手机端需要延迟执行动画，暂时没用到
    addCartType?: string;//加车类型
    onAddCartSuccess?: () => void;
    onAddCartFail?: () => void;
    onChangeVariant?: (variant: IVariant) => Promise<void>;
}

/**
 * 根据选中的 variant id 获取 groups
 * @param variantId
 * @param productOptionList
 */
const getGroupsByVariantId = (variantId: string, productOptionList: IGroup[]) => {
    return productOptionList.map((group) => {
        return {
            ...group,
            checkedOption: group.productOptionValues.find((option) => {
                return option.productVariantIds.includes(variantId);
            })
        }
    });
};

// 获取单个group可选的variant id，取所有id 和 其它group已选项 的交集
const getGroupActiveIds = (groups: IGroup[], excludeIndex: number, allIds: string[]): string[] => {
    let ids: string[] = allIds;
    if (groups.length > 1) {
        groups.forEach((group, index) => {
            if (index !== excludeIndex && group.checkedOption) {
                ids = getIntersection(ids, group.checkedOption.productVariantIds);
            }
        });
    }
    return ids;
};

/**
 * 到点击目标group层为止，只取上层groups的做集合交集
 * @param groups
 * @param targetIndex
 * @param allIds
 */
const getGroupActiveIdsWithPreGroups = (groups: IGroup[], targetIndex: number, allIds: string[]): string[] => {
    let ids: string[] = allIds;

    for (let groupIndex in groups) {
        const index = +groupIndex
        const group = groups[index]
        if (index >= targetIndex) {
            break;
        } else if (group.checkedOption) {
            ids = getIntersection(ids, group.checkedOption.productVariantIds);
        }
    }

    return ids;
};

// 获取 id 数组交集
const getIntersection = (arr1: string[], arr2: string[]): string[] => {
    return arr1.filter(item => arr2.includes(item));
}

// 判断选项状态，是否可点击，是否有库存
const getRenderGroups = (groups: IGroup[], allIds: string[], onSaleIds: string[]): IGroup[] => {
    return groups.map((group, index) => {
        // const activeIds = getGroupActiveIds(groups, index, allIds); // 可点击的
        const activeIds = getGroupActiveIdsWithPreGroups(groups, index, allIds); // 可点击的

        return {
            ...group,
            productOptionValues: group.productOptionValues.map(option => {
                    const disabled = getIntersection(activeIds, option.productVariantIds).length === 0
                    const outOfStock = getIntersection(onSaleIds, option.productVariantIds).length === 0

                    return {
                        ...option,
                        disabled: disabled,
                        outOfStock: outOfStock,
                    }
                }
            )
        }
    });
};

const getDefaultGift = (variant: IVariant): IGift | undefined => {
    if (!variant?.gifts?.detail?.options?.length) {
        return;
    }
    const options = variant.gifts.detail.options
    return options.find(item => item.default) || options[0];
};

export default function useAddCart(params: IUseAddCartParams): IAddCartInstance {
    const {
        module,
        trackModuleId,
        spuInfo: spu,
        defaultThumbSize = 100,
        isHiddenOption = false,
        changeUrlWhenCheckOption = true,
        disableBuyNow = false,
        getThumbSrc,
        animateDelay = 0,
        addCartType,
        onAddCartSuccess,
        onAddCartFail,
        showBanner = true,
        onChangeVariant,
    } = params || {};

    const ariaLive = useAriaLive({
        id: 'add-cart-aria-live',
        options: {
            ariaLive: 'assertive'
        }
    })
    const { onSetAriaLiveContent } = ariaLive

    const { ua: { isPhone } } = getStoreState('rootStore');

    const { userStore, miniCartStore, rootStore } = useStore();
    const isDealer = userStore.isDealer();

    const { productVariants: variants = [], productOptionList = [], productType } = spu || {};

    // 只有一个sku，或者一个属性、一个选项时，隐藏表单，直接提交
    const isFormHidden = isHiddenOption || (variants?.length < 2) || (productOptionList.length === 1 && productOptionList[0].productOptionValues.length === 1);

    const router = useRouter();
    // 所有variant id
    const allIds = variants.map(item => item.productVariantId);
    // 未下架且有库存的variant id
    const onSaleIds = getOnSaleIds(variants);
    // 默认选中的variant id
    const defaultVariant: IVariant = getDefaultVariant(spu);
    // 已选中variant
    const [checkedVariant, setCheckedVariant] = useState<IVariant | undefined>(defaultVariant);
    // 当前展示的variant（当前选中的或上一个选中的）
    const [shownVariant, setShownVariant] = useState<IVariant>(defaultVariant);
    // 选项
    const [groups, setGroups] = useState<IGroup[]>(getRenderGroups(getGroupsByVariantId(defaultVariant?.productVariantId, productOptionList), allIds, onSaleIds));
    // 商品数量
    const [qty, setQty] = useState<number>(1);
    // 选中配件的variantId
    const [checkedAddOns, setCheckedAddOns] = useState<string[]>([]);
    const [checkedGift, setCheckedGift] = useState<IGift | undefined>(getDefaultGift(defaultVariant));

    // 提示未选择错误
    const [isErrorShown, setIsErrorShown] = useState<boolean>(false);
    // 表单提交状态
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [buyNowLoading, setBuyNowLoading] = useState<boolean>(false);
    const [availableCoupons, setAvailableCoupons] = useState<ICoupon[]>([]);

    const [feature, setFeature] = useState<Record<string, IFeature[]>>({})

    // 记录 切换variant 的feature配置
    const featuresRef = useRef<Record<string, Record<string, IFeature[]>>>()

    /**
     * 计算coupon优惠价格 取第一位展示
     */
    const calculateCouponDiscount = async (variant: IVariant, qty = 1) => {
        const couponRes = await calculateCouponList({
            variantNumList: [{
                variantId: variant.variantId,
                num: qty,
                usePerProductDiscount: !!variant.discountPriceNumDisplay ? 1 : 0,
            }],
            page: 'detail',
        })
        couponRes && setAvailableCoupons(couponRes.available);
    }


    /**
     * 获取变体feature 配置
     */
    const getVariantFeatureHandle = async (variantId) => {
        if (!showBanner) {
            return;
        }
        if (featuresRef.current?.[variantId]) {
            setFeature(featuresRef.current[variantId])

        } else {
            try {
                const res = await getDetailFeatureConfig({
                    name: isPhone ? 'DetailPageH5_01' : 'DetailPagePc_01',
                    variantId: variantId,
                })

                const locationFeature = {}

                res.forEach((feature) => {
                    const locationCode = (feature as IOldFeatureBanner).config?.locationCode?.trim() || ''
                    if (!locationFeature.hasOwnProperty(locationCode)) {
                        locationFeature[locationCode] = [feature]
                    } else {
                        locationFeature[locationCode] = [...locationFeature[locationCode], feature]
                    }
                })

                setFeature(locationFeature)

                featuresRef.current = {
                    ...featuresRef.current,
                    [variantId]: locationFeature
                }

            } catch (e) {
            }
        }
    }

    /**
     * 遍历groups，更新disable状态，判断是否已选中 variant
     * @showId: 设置直接展示的id
     */
    const updateRenderData = (newGroups: IGroup[]) => {
        let item: IVariant | undefined;
        // 更新disable状态
        const _groups = getRenderGroups(newGroups, allIds, onSaleIds);
        setGroups(_groups);
        // 判断是否已选中 variant
        let checkedGroups = _groups.filter(group => group.checkedOption);
        if (checkedGroups.length === _groups.length) {
            let checkedIds: string[] = allIds;
            checkedGroups.forEach(group => {
                checkedIds = getIntersection(checkedIds, (group.checkedOption as IOption).productVariantIds)
            });

            item = variants.find(variant => variant.productVariantId === checkedIds[0]);
        }
        setCheckedVariant(item);
        if (item && item.variantId !== shownVariant.variantId) {
            // 获取feature配置
            setShownVariant(item);
            getVariantFeatureHandle(item.variantId).then()
            setCheckedAddOns([]);
            calculateCouponDiscount(item, qty).then()
            onChangeVariant?.(item);
            if (changeUrlWhenCheckOption) {
                router.replace(item.url, item.url, { shallow: true });
            }
        }
    };

    const onCheckOption = (groupIndex: number, option: IOption) => {
        let _groups: IGroup[] = [...groups];
        const group = _groups[groupIndex];
        // 选项是否时已选中
        const checked = group.checkedOption && group.checkedOption.optionValueId === option.optionValueId

        if (checked) {
            return
        }
        if (option.disabled && !checked) {
            /**
             * disabled 重置 variantId 重选option
             */
            const currentVariantIds = option.productVariantIds
            const currentOnSaleVIds = getIntersection(onSaleIds, currentVariantIds)
            const newVariantId = currentOnSaleVIds?.[0] || currentVariantIds[0]

            _groups = getRenderGroups(
                getGroupsByVariantId(newVariantId, productOptionList),
                allIds,
                onSaleIds
            )

        } else {
            /**
             * 如果当前点击的option是无货状态
             *  variantId切换至该option第一个有库存的option
             *  再是该option variantIds列表第一个非disabled option
             */
            _groups[groupIndex] = {
                ..._groups[groupIndex],
                checkedOption: option
            };
            _groups.forEach((gGroup, gIndex) => {
                // 将点击当前group组以下的 组别判断重选option
                if (gIndex > groupIndex) {
                    const options = gGroup.productOptionValues
                    const checkedOptionVariantIds = gGroup.checkedOption?.productVariantIds || []
                    const preActiveIds = getGroupActiveIdsWithPreGroups(_groups, gIndex, allIds)
                    const checkedDisabled = getIntersection(preActiveIds, checkedOptionVariantIds).length === 0

                    // const outOfStock = getIntersection(onSaleIds, checkedOptionVariantIds).length === 0

                    let newCheckedOption = gGroup.checkedOption

                    // 已选过 but 现在属于disabled
                    if (checkedDisabled && checkedOptionVariantIds.length) {
                        // 有库存的
                        newCheckedOption = options.find((oOption) => {
                            let opActiveIds = getIntersection(preActiveIds, oOption.productVariantIds)
                            let opOnSaleIds = getIntersection(onSaleIds, opActiveIds || [])
                            return opOnSaleIds.length
                        })
                        // 符合pre选择组合的
                        if (!newCheckedOption) {
                            newCheckedOption = options.find((oOption) => {
                                let opActiveIds = getIntersection(preActiveIds, oOption.productVariantIds)
                                return opActiveIds.length
                            })
                        }
                    }

                    _groups[gIndex] = {
                        ...gGroup,
                        checkedOption: newCheckedOption,
                    }
                }
            })
        }

        updateRenderData(_groups);
    };

    /**
     * 获取打点 variant 数组
     * @checkedVariant: 当前选中的variant
     */
    const getTrackItems = (checkedVariant: IVariant): ITrackVariant[] => {
        const addOnsVariants: IProduct[] = checkedVariant.addOns
            .filter(item => checkedAddOns.includes(item.variantId))
            .map(item => ({ ...item, num: qty }));

        return [
            {
                variantId: checkedVariant.variantId,
                trackId: checkedVariant.trackId,
                productTrackCode: checkedVariant.productTrackCode,
                variantTrackCode: checkedVariant.variantTrackCode,
                categoryTrackCode: checkedVariant.categoryTrackCode,
                shopPrice: checkedVariant.shopPrice,
                dealPrice: checkedVariant.dealPrice,
                num: qty,
            },
            ...addOnsVariants,
        ];
    };

    const onChangeQty = (num: number) => {
        if (rootStore.state.query[DEBUG_SOURCEMAP] === '1') {
            // @ts-ignore
            console.log(testUndefinedVar);
        }
        setQty(num)
        calculateCouponDiscount(shownVariant, num).then()
        onSetAriaLiveContent(`${num} ${
            checkedVariant?.productName ? checkedVariant?.productName : 'items'
        }`)
    }

    const onChangeCheckedAddOns = (ids: string[]) => {
        setCheckedAddOns(ids)
    }

    // 传参商品 提交 to Cart
    const onSubmitWithVariant = async (variant: IVariant | IProduct, qty: number) => {
        return postAddCart([
            {
                productVariantId: (variant as IVariant)?.productVariantId
                    || (variant as IProduct)?.variantId,
                num: qty
            }
        ]).then(res => {
            if (!res.addFailProductName || res.addFailProductName.length === 0) {
                miniCartStore.afterAddCart(1);
                onAddCartSuccess?.()
            }
        })
    }

    const onSubmit = async () => {
        if (checkedVariant) {
            trackAddToCart(getTrackItems(checkedVariant));

            setSubmitting(true);
            return postAddCart([
                // 主商品
                {
                    productVariantId: checkedVariant.productVariantId,
                    num: qty
                },
                // 配件
                ...checkedAddOns.map(item => ({
                    productVariantId: item,
                    num: qty
                }))
            ], addCartType).then(res => {
                onSetAriaLiveContent('loading')
                if (!res.addFailProductName || res.addFailProductName.length === 0) {
                    let src = getThumbSrc?.();
                    if (!src) {
                        src = getVariantImageSrc(checkedVariant.pics, checkedVariant.productVariantId, defaultThumbSize);
                    }
                    const multiSrcList = [src, ...getAddOnsSrcList() as string[]];

                    if (onAddCartSuccess) {
                        onAddCartSuccess?.()
                    } else {
                        // 下拉，mini Cart动画
                        if (animateDelay) {
                            setTimeout(() => {
                                miniCartStore.afterAddCart(qty, multiSrcList);
                            }, animateDelay);
                        } else {
                            miniCartStore.afterAddCart(qty, multiSrcList);
                        }
                    }

                } else {
                    onAddCartFail?.();
                    Message.error(`${res.addFailProductName[0]} is temporarily unavailable.`);
                }

                const summary = getVariantSummary(shownVariant, checkedAddOns, qty);

                const price1 = formatPrice(summary.totalPrice)
                setTimeout(() => {
                    onSetAriaLiveContent(
                        `Adding ${qty} ${checkedVariant.productName}, total: ${price1.currencySymbol}${price1.integer}${price1.decimal}`)
                }, 0.5 * 1000)
            }).catch(err => {
                onAddCartFail?.();
                if (err.code == CODE_FLUSH_PAGE) {
                    msgModal({
                        content: err.message,
                        okText: 'RETURN',
                        afterClose: () => {
                            router.reload();
                        }
                    });
                } else {
                    Message.error(err.message);
                }

            }).finally(() => {
                setSubmitting(false);
            });

        } else {
            setIsErrorShown(true);
            return Promise.reject();
        }
    };

    const onBuyNow = async () => {
        if (checkedVariant) {
            let variantNums = [{ variantId: checkedVariant.productVariantId, num: qty }];
            //预售商品 gift 就是组合商品，不需要包含主商品
            if (checkedGift) {
                variantNums = [{ variantId: checkedGift.variantId, num: qty }];
            }

            setBuyNowLoading(true);
            try {
                // await checkBuyNow(variantNums);
                await checkBuyNowHandle(variantNums);

                // 多页面操作判断 保存的cartIds
                let price = Number(checkedVariant.dealPrice || 0);
                if (price === 0) {
                    price = checkedVariant.shopPrice;
                }
                buySkipCart({
                    revenue: (price * qty).toFixed(2),
                    trackItems: getTrackItems(checkedVariant),
                    storageItems: [
                        // 主商品 + gift
                        ...variantNums,
                        // 配件
                        ...checkedAddOns.map(item => ({
                            variantId: item,
                            num: qty
                        }))
                    ]
                });
            } catch (e) {
                msgModal({
                    content: e.message,
                });
            }

            setBuyNowLoading(false);

        } else {
            setIsErrorShown(true);
        }
    };

    // 商品页点击其他商品链接，需要重置state
    const resetState = () => {
        const defaultVariant = getDefaultVariant(spu);
        setCheckedVariant(defaultVariant);
        setShownVariant(defaultVariant);
        getVariantFeatureHandle(defaultVariant.variantId).then()
        setGroups(getRenderGroups(getGroupsByVariantId(defaultVariant?.productVariantId, productOptionList), allIds, onSaleIds));
        setQty(1);
        setCheckedAddOns([]);
        setIsErrorShown(false);
        setSubmitting(false);
        calculateCouponDiscount(defaultVariant, 1).then();
        setCheckedGift(getDefaultGift(defaultVariant));

        if (module === 'detail') {
            trackPageView({ detail: defaultVariant?.variantTrackCode });
        }

        trackProductImpression({
            variantId: defaultVariant.variantId,
            trackId: defaultVariant.trackId,
            productTrackCode: defaultVariant.productTrackCode,
            variantTrackCode: defaultVariant.variantTrackCode,
            categoryTrackCode: defaultVariant.categoryTrackCode,
            shopPrice: defaultVariant.shopPrice,
            dealPrice: defaultVariant.dealPrice,
            listTrackCode: module === 'detail' ? 'detail' : 'quickview',
        });
    };

    useEffect(() => {
        rootStore.update({ pageLoading: false });
    }, [shownVariant]);

    useEffect(() => {
        if (!!spu) {
            resetState();
        }
    }, [spu]);

    return {
        trackModuleId,
        variants,
        onSaleIds,
        isFormHidden,
        disableBuyNow,
        groups,
        shownVariant,
        checkedVariant,
        onCheckOption,
        qty,
        onChangeQty,
        checkedAddOns,
        onChangeCheckedAddOns,
        checkedGift,
        setCheckedGift,
        isErrorShown,
        onBuyNow,
        onSubmit,
        onSubmitWithVariant,
        submitting,
        buyNowLoading,
        outOfStock: onSaleIds.length === 0 || !!(checkedVariant && !onSaleIds.includes(checkedVariant.productVariantId)),
        productType,
        isDealer,
        availableCoupons,
        feature
    }
}